/**
 * S3 Analytics GraphQL Operations
 *
 * GraphQL queries and mutations for the new S3-based analytics system.
 * These operations replace the old DynamoDB-based analytics queries.
 */

import { gql } from "@apollo/client";

// Dashboard metrics query
export const GET_S3_DASHBOARD_METRICS = gql`
  query GetS3DashboardMetrics(
    $tenantId: String!
    $dateFrom: String!
    $dateTo: String!
    $deviceType: String
    $country: String
  ) {
    getS3DashboardMetrics(
      query: {
        tenantId: $tenantId
        dateFrom: $dateFrom
        dateTo: $dateTo
        deviceType: $deviceType
        country: $country
      }
    ) {
      tenantId
      dateRange {
        from
        to
      }
      totalClicks
      uniqueClicks
      totalLinks
      activeLinks
      conversionRate
      deviceBreakdown {
        MOBILE
        DESKTOP
        TABLET
        OTHER
      }
      locationBreakdown {
        country
        clicks
        percentage
      }
      topPerformingLinks {
        linkId
        shortUrl
        clicks
        uniqueClicks
        conversionRate
      }
      recentActivity {
        eventId
        eventType
        timestamp
        linkId
        deviceType
      }
      trendsData {
        date
        clicks
        uniqueClicks
      }
    }
  }
`;

// Link-specific analytics query
export const GET_S3_LINK_ANALYTICS = gql`
  query GetS3LinkAnalytics(
    $linkId: String!
    $tenantId: String!
    $dateFrom: String!
    $dateTo: String!
    $deviceType: String
    $country: String
  ) {
    getS3LinkAnalytics(
      linkId: $linkId
      query: {
        tenantId: $tenantId
        dateFrom: $dateFrom
        dateTo: $dateTo
        deviceType: $deviceType
        country: $country
      }
    ) {
      linkId
      shortUrl
      originalUrl
      dateRange {
        from
        to
      }
      totalClicks
      uniqueClicks
      conversionRate
      averageSessionDuration
      deviceBreakdown {
        MOBILE
        DESKTOP
        TABLET
        OTHER
      }
      locationBreakdown {
        country
        region
        clicks
        percentage
      }
      hourlyBreakdown {
        hour
        clicks
        uniqueClicks
      }
      dailyBreakdown {
        date
        clicks
        uniqueClicks
      }
      referrerBreakdown {
        referrer
        clicks
        percentage
      }
      eventTimeline {
        eventId
        eventType
        timestamp
        deviceType
        location
      }
    }
  }
`;

// Top performing links query
export const GET_S3_TOP_PERFORMING_LINKS = gql`
  query GetS3TopPerformingLinks(
    $tenantId: String!
    $dateFrom: String!
    $dateTo: String!
    $limit: Int = 10
  ) {
    getS3TopPerformingLinks(
      query: {
        tenantId: $tenantId
        dateFrom: $dateFrom
        dateTo: $dateTo
        limit: $limit
      }
    ) {
      linkId
      shortUrl
      totalClicks
      uniqueClicks
      firstClick
      lastClick
      conversionRate
    }
  }
`;

// Top performing conversations query
export const GET_S3_TOP_PERFORMING_CONVERSATIONS = gql`
  query GetS3TopPerformingConversations(
    $tenantId: String!
    $dateFrom: String!
    $dateTo: String!
    $limit: Int = 10
  ) {
    getS3TopPerformingConversations(
      query: {
        tenantId: $tenantId
        dateFrom: $dateFrom
        dateTo: $dateTo
        limit: $limit
      }
    ) {
      conversationId
      conversationName
      clicks
      uniqueClicks
      uniqueLinks
      conversionRate
      firstClick
      lastClick
    }
  }
`;

// Top performing assets query
export const GET_S3_TOP_PERFORMING_ASSETS = gql`
  query GetS3TopPerformingAssets(
    $tenantId: String!
    $dateFrom: String!
    $dateTo: String!
    $limit: Int = 10
  ) {
    getS3TopPerformingAssets(
      query: {
        tenantId: $tenantId
        dateFrom: $dateFrom
        dateTo: $dateTo
        limit: $limit
      }
    ) {
      assetId
      assetName
      clicks
      uniqueClicks
      uniqueLinks
      conversionRate
      firstClick
      lastClick
    }
  }
`;

// Hourly trends query
export const GET_S3_HOURLY_TRENDS = gql`
  query GetS3HourlyTrends(
    $tenantId: String!
    $dateFrom: String!
    $dateTo: String!
    $linkId: String
  ) {
    getS3HourlyTrends(
      query: {
        tenantId: $tenantId
        dateFrom: $dateFrom
        dateTo: $dateTo
        linkId: $linkId
      }
    ) {
      date
      hour
      clicks
      uniqueClicks
    }
  }
`;

// Analytics query cost estimation
export const ESTIMATE_QUERY_COST = gql`
  query EstimateQueryCost(
    $tenantId: String!
    $dateFrom: String!
    $dateTo: String!
    $queryType: String!
  ) {
    estimateS3QueryCost(
      query: { tenantId: $tenantId, dateFrom: $dateFrom, dateTo: $dateTo }
      queryType: $queryType
    ) {
      estimatedCostUSD
      estimatedDataScanGB
      estimatedExecutionTimeMs
      partitionsToScan
      recommendations
    }
  }
`;

// Real-time metrics query (from DynamoDB)
export const GET_REAL_TIME_METRICS = gql`
  query GetRealTimeMetrics($tenantId: String!, $date: String!) {
    getRealTimeMetrics(tenantId: $tenantId, date: $date) {
      totalClicks
      uniqueClicks
      deviceBreakdown {
        MOBILE
        DESKTOP
        TABLET
        OTHER
      }
      linkCount
      lastUpdated
    }
  }
`;

// Analytics cache management
export const CLEAR_ANALYTICS_CACHE = gql`
  mutation ClearAnalyticsCache($tenantId: String!) {
    clearAnalyticsCache(tenantId: $tenantId) {
      success
      message
      clearedEntries
    }
  }
`;

// Analytics export mutation
export const EXPORT_ANALYTICS_DATA = gql`
  mutation ExportAnalyticsData(
    $tenantId: String!
    $dateFrom: String!
    $dateTo: String!
    $format: String! # CSV, JSON, PARQUET
    $includeRawEvents: Boolean = false
  ) {
    exportAnalyticsData(
      query: { tenantId: $tenantId, dateFrom: $dateFrom, dateTo: $dateTo }
      format: $format
      includeRawEvents: $includeRawEvents
    ) {
      exportId
      downloadUrl
      expiresAt
      fileSizeBytes
      recordCount
      status
    }
  }
`;

// Analytics subscription for real-time updates
export const ANALYTICS_REAL_TIME_UPDATES = gql`
  subscription AnalyticsRealTimeUpdates($tenantId: String!) {
    analyticsRealTimeUpdates(tenantId: $tenantId) {
      eventType
      timestamp
      linkId
      deviceType
      location
      incrementalMetrics {
        totalClicks
        uniqueClicks
        deviceBreakdown {
          MOBILE
          DESKTOP
          TABLET
          OTHER
        }
      }
    }
  }
`;

// TypeScript interfaces for the operations
export interface DashboardMetricsVariables {
  tenantId: string;
  dateFrom: string;
  dateTo: string;
  deviceType?: string;
  country?: string;
}

export interface LinkAnalyticsVariables {
  linkId: string;
  tenantId: string;
  dateFrom: string;
  dateTo: string;
  deviceType?: string;
  country?: string;
}

export interface TopPerformingLinksVariables {
  tenantId: string;
  dateFrom: string;
  dateTo: string;
  limit?: number;
}

export interface HourlyTrendsVariables {
  tenantId: string;
  dateFrom: string;
  dateTo: string;
  linkId?: string;
}

export interface ExportAnalyticsVariables {
  tenantId: string;
  dateFrom: string;
  dateTo: string;
  format: "CSV" | "JSON" | "PARQUET";
  includeRawEvents?: boolean;
}

// Response type interfaces
export interface DashboardMetrics {
  tenantId: string;
  dateRange: {
    from: string;
    to: string;
  };
  totalClicks: number;
  uniqueClicks: number;
  totalLinks: number;
  activeLinks: number;
  conversionRate: number;
  deviceBreakdown: {
    MOBILE: number;
    DESKTOP: number;
    TABLET: number;
    OTHER: number;
  };
  locationBreakdown: Array<{
    country: string;
    clicks: number;
    percentage: number;
  }>;
  topPerformingLinks: Array<{
    linkId: string;
    shortUrl: string;
    clicks: number;
    uniqueClicks: number;
    conversionRate: number;
  }>;
  recentActivity: Array<{
    eventId: string;
    eventType: string;
    timestamp: string;
    linkId: string;
    deviceType: string;
  }>;
  trendsData: Array<{
    date: string;
    clicks: number;
    uniqueClicks: number;
  }>;
}

export interface LinkAnalytics {
  linkId: string;
  shortUrl: string;
  originalUrl: string;
  dateRange: {
    from: string;
    to: string;
  };
  totalClicks: number;
  uniqueClicks: number;
  conversionRate: number;
  averageSessionDuration: number;
  deviceBreakdown: {
    MOBILE: number;
    DESKTOP: number;
    TABLET: number;
    OTHER: number;
  };
  locationBreakdown: Array<{
    country: string;
    region: string;
    clicks: number;
    percentage: number;
  }>;
  hourlyBreakdown: Array<{
    hour: number;
    clicks: number;
    uniqueClicks: number;
  }>;
  dailyBreakdown: Array<{
    date: string;
    clicks: number;
    uniqueClicks: number;
  }>;
  referrerBreakdown: Array<{
    referrer: string;
    clicks: number;
    percentage: number;
  }>;
  eventTimeline: Array<{
    eventId: string;
    eventType: string;
    timestamp: string;
    deviceType: string;
    location?: string;
  }>;
}
